import { ObservableModelNode } from "./observable";
import {
  collectProductGrayscaleAssetPaths,
  collectProductRGBAAssetPaths,
  type SpecsModelAssetPath,
} from "./productGrayscaleAssets";
import { VersionModel } from "./VersionModel";
import {
  cloneValue,
  expectObject,
  readObjectArray,
  readString,
  splitKnown,
} from "./utils";
import type { JsonObject } from "./utils";
import { DEFAULT_PRODUCT_VERSION_STATUS } from "./productMetadata/productMetadataEnums";

export class ProductModel extends ObservableModelNode {
  name!: string;
  versions!: VersionModel[];
  extra!: JsonObject;

  constructor() {
    super();
    this.defineObservableValue("name", "");
    this.defineObservableValue("versions", []);
    this.defineObservableValue("extra", {});
  }

  restore(data: unknown) {
    const source = expectObject(data, "product");

    this.extra = splitKnown(source, ["name", "versions"]);
    this.name = readString(source, "name");
    this.versions = readObjectArray(source, "versions").map((version) =>
      new VersionModel().restore(version)
    );

    return this;
  }

  getVersion(name: string) {
    return this.versions.find((version) => version.name === name);
  }

  getDisplayName() {
    return typeof this.extra.displayName === "string"
      ? this.extra.displayName
      : "";
  }

  setDisplayName(displayName: string) {
    this.extra = {
      ...this.extra,
      displayName,
    };
  }

  addVersion(name: string) {
    const versionName = name.trim();
    if (!versionName || this.getVersion(versionName)) {
      return null;
    }

    const version = new VersionModel();
    version.name = versionName;
    version.setRawVersionMetadata({ status: DEFAULT_PRODUCT_VERSION_STATUS });
    this.versions.push(version);
    return version;
  }

  save() {
    return {
      ...cloneValue(this.extra),
      name: this.name,
      versions: this.versions.map((version) => version.save()),
    };
  }

  getGrayscaleAssetPaths(): SpecsModelAssetPath[] {
    return collectProductGrayscaleAssetPaths(this);
  }

  getRGBAAssetPaths(): SpecsModelAssetPath[] {
    return collectProductRGBAAssetPaths(this);
  }
}
